import React, { useState, useEffect } from "react";
import { useGet } from "seed/api";
import { useQuery, useDelete, useDetail, useSet } from "seed/gql";
import { Loading } from "seed/helpers";
import { SET_PARTICIPANT } from "seed/gql/queries";
import { DELETE_PARTICIPANT } from "seed/gql/queries";
import { APP_URL } from "settings";
import { usePermissions } from "components/util/helpers/providers/PermissionsProvider";
import { groupParticipantsByRole } from "components/presentation/forms/collaboration/Collaboration.lib.groups";
import View from "components/presentation/forms/collaboration/Collaboration.view";

function Collaboration({
  flowId,
  onlyAdminRoles
}) {

  const userId = sessionStorage.getItem("id");
  const pageSize = 10;
  
  const { isAdmin } = usePermissions();
  const [isAccessModelOpen, setIsAccessModelOpen] = useState(false);
  const [isPermissionModelOpen, setIsPermissionModelOpen] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  let rolesFilter = `flow.id=${flowId}`;
  if (onlyAdminRoles != null)
    rolesFilter += ` AND is_admin=${onlyAdminRoles ? 'true' : 'false'}`;

  const reqRoles = useQuery(`{
    participantRoles {
      name
    }
  }`, rolesFilter,
  {
    notifyOnNetworkStatusChange: false
  });

  const reqUsers = useQuery(`{
    users {
      email
      firstName
      lastName
      username
      participants {
        flow { name }
      }
    }
  }`, `id <> ${userId} AND isAnonymous=False`, {
    notifyOnNetworkStatusChange: false
  })

  const reqFlow = useDetail(`{
    flow {
      name
      token
      settings
      user {
        firstName
        lastName
        email
      }
    } 
  }`, flowId, {
    notifyOnNetworkStatusChange: false
  });

  const reqParticipants = useGet("/flows/get_participants", {
    flow_id: flowId,
    role_id: selectedRoleId,
    search: searchQuery,
    page_num: pageNum,
    page_size: pageSize,
  });

  const reqOrganizations = useQuery(`{
    organizations {
      name
      owner { 
        username 
      }
    }
  }`, {
    notifyOnNetworkStatusChange: false
  });

  const [callSetRoleParticipant, reqSetRoleParticipant] = useSet(SET_PARTICIPANT)

  const [callDelete] = useDelete(DELETE_PARTICIPANT, {
    onCompleted: () => {
      reqUsers.refetch();
      reqParticipants.refetch();
    },
    onError: (err) => {
      console.log(err);
    }
  });

  useEffect(() => {
    reqRoles.refetch();
    reqUsers.refetch();
    reqFlow.refetch();
    reqOrganizations.refetch();
  }, []);

  useEffect(() => {
    reqParticipants.refetch();
  }, [pageNum, selectedRoleId, searchQuery]);

  // Waits for the user to stop typing so every keystroke does not fire a request
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
      setPageNum(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const onCloseAccessModel = () =>
    setIsAccessModelOpen(false);

  const onOpenAccessModel = () =>
    setIsAccessModelOpen(true);

  const onClickClosePermissionModel = () =>
    setIsPermissionModelOpen(false);

  const onClickOpenPermissionModel = () =>
    setIsPermissionModelOpen(true);

  const onClickChangeOrganizationParticipantRole = (participant, orgParticipant, newRole) => {
    const settings = Object.assign({}, participant.settings || {});
    settings.roles = Object.assign({}, settings.roles || {});
    settings.roles[orgParticipant.user.id] = parseInt(newRole);
    callSetRoleParticipant({
      id: participant.id,
      settings
    });
  }

  const onChangeParticipantRole = (participantId, newRole) => {
    callSetRoleParticipant({
      id: participantId,
      role: parseInt(newRole)
    })
  }

  const onClickRemoveParticipant = (participantId) => {
    callDelete({ id: participantId });
  }

  const onChangeSearch = (event) =>
    setSearchInput(event.target.value);

  const onChangeRole = (event) => {
    setSelectedRoleId(event.target.value);
    setPageNum(1);
  }

  const onClickPage = (page) =>
    setPageNum(page);

  const onCompleteAccess = () => {
    setIsAccessModelOpen(false);
    reqUsers.refetch();
    reqParticipants.refetch();
  }

  if (reqRoles.error || reqUsers.error || reqParticipants.error || reqFlow.error || reqOrganizations.error) return <div />;
  // `loaded` rather than `loading` for participants: a refetch keeps the previous page on screen,
  // while `loading` blinks and would unmount the search box mid-typing.
  if (reqRoles.loading || reqUsers.loading || !reqParticipants.loaded || reqFlow.loading || reqOrganizations.loading) return <Loading />;

  const { participants, totalPages } = reqParticipants.data;
  const participantGroups = groupParticipantsByRole(participants);

  const { participantRoles = [] } = reqRoles.data;
  const { flow = {} } = reqFlow.data
  const flowUrl = decodeURIComponent(`${APP_URL}/${flow.token}`)

  const roleOptions = participantRoles.map(role => ({
    value: role.id,
    label: role.name
  }));

  return (
    <View
      flow={flow}
      flowUrl={flowUrl}
      pageNum={pageNum}
      participantGroups={participantGroups}
      roleOptions={roleOptions}
      reqParticipants={reqParticipants}
      search={searchInput}
      selectedRoleId={selectedRoleId}
      totalPages={totalPages}
      isAdmin={isAdmin}
      isAccessModelOpen={isAccessModelOpen}
      isPermissionModelOpen={isPermissionModelOpen}
      onCompleteAccess={onCompleteAccess}
      onChangeParticipantRole={onChangeParticipantRole}
      onChangeSearch={onChangeSearch}
      onChangeRole={onChangeRole}
      onClickPage={onClickPage}
      onClickChangeOrganizationParticipantRole={onClickChangeOrganizationParticipantRole}
      onClickRemoveParticipant={onClickRemoveParticipant}
      onOpenAccessModel={onOpenAccessModel}
      onCloseAccessModel={onCloseAccessModel}
      onClickOpenPermissionModel={onClickOpenPermissionModel}
      onClickClosePermissionModel={onClickClosePermissionModel}
    />
  );
}

export default Collaboration;